import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';
import { ChevronDown, BookOpen, Sparkles } from 'lucide-react';
import { cn } from '../lib/utils';

const articleContent = {
  he: {
    title: 'Microlit – ציפוי מינרלי אחיד לכל תשתית',
    subtitle: 'מאמר מערכת',
    readMore: 'קרא עוד',
    readLess: 'סגור',
    paragraphs: [
      'Microlit הוא ציפוי מינרלי מוכן לשימוש, שפותח מתוך ראייה רחבה של עולם חומרי הבנייה המודרני, עולם שבו תשתיות כבר אינן אחידות. בטון פוגש קלקר, לוחות בנייה משתלבים עם הדפסות תלת־ממד, ואלמנטים מודפסים משתלבים בבנייה מסורתית. האתגר אינו רק חיבור פיזי בין החומרים, אלא יצירת שפה חומרית אחת, אחידה וטבעית.',
      'הבסיס של Microlit הוא תערובת מינרלית טבעית בשילוב פולימרים מתקדמים ומוצלבים, המעניקים לחומר יכולת הידבקות גבוהה, גמישות מבוקרת ועמידות לאורך זמן. שילוב זה מאפשר יישום ישיר גם על תשתיות שאינן מינרליות במהותן, לרבות הדפסות תלת־ממד מחומרים שונים, קלקר מוקצף, לוחות בנייה קלים ואלמנטים מורכבים.',
      'בעולם ההדפסות התלת־ממדיות, Microlit מאפשר מעבר מחומר טכני גולמי למראה אדריכלי טבעי. אלמנטים מודפסים ב־PLA, PETG או חומרים אחרים יכולים לקבל שכבת ציפוי מינרלית דקה, אחידה ומט, שמעלימה את סימני ההדפסה ומעניקה תחושת אבן, טיח או חומר טבעי, מבלי לפגוע בגיאומטריה או בפרטים.',
      'המערכת אינה מסתפקת בציפוי בלבד. לצד Microlit קיימת מערכת חומרים מקשרים ייעודית, שתפקידה לאחד בין תשתיות שונות וליצור שכבת ביניים יציבה. כך ניתן לעבוד על שילובים מורכבים של קלקר ובטון, אלמנטים מודפסים לצד קירות קיימים, או תוספות חדשות על גבי מבנה ישן, ולקבל מראה אחיד ורציף ללא הבדלי חומר או צבע.',
      'Microlit מיושם כשכבת גמר דקה, בדומה למיקרוטופינג מינרלי, אך בנוחות של חומר מוכן לשימוש. אין צורך בתוספים, ערבובים מסובכים או התאמות מיוחדות לכל תשתית. המערכת כולה נבנתה כך שהחומר "יודע" להתאים את עצמו, והמשתמש יכול להתמקד בביצוע ובתוצאה.',
      'הגימור המתקבל הוא גמר מט טבעי, בעל מרקם מינרלי חי ולא אחיד במכוון. הגוון אינו מתקבל מפיגמנטים סינתטיים, אלא מהמינרלים עצמם: מיקה, חרסית, גיר, טיטניום ומינרלים טבעיים נוספים. כך מתקבלת שכבת צבע שאינה נראית כצבע, אלא כחומר.',
      'היכולת לאחד תשתיות שונות תחת שכבה מינרלית אחת הופכת את Microlit לכלי עבודה מרכזי בפרויקטים עכשוויים: חיפויי קירות, אלמנטים דקורטיביים, חידוש חללים, עבודות DIY מתקדמות, ושילוב הדפסות תלת־ממד בבנייה ובעיצוב פנים וחוץ.',
      'Microlit אינו חומר שמסתיר תשתיות, אלא חומר שמתרגם אותן לשפה אחת. הוא מאפשר לעבוד עם חומרים חדשניים וטכנולוגיים, מבלי לוותר על המראה הטבעי והעל־זמני של חומר מינרלי. זהו חיבור מדויק בין בנייה מסורתית, טכנולוגיה מתקדמת וחוויית עבודה מודרנית, שמייצר תוצאה אחידה, רגועה ונכונה לאורך זמן.'
    ]
  },
  en: {
    title: 'Microlit – Uniform Mineral Coating for Any Substrate',
    subtitle: 'System Article',
    readMore: 'Read More',
    readLess: 'Close',
    paragraphs: [
      'Microlit is a ready-to-use mineral coating, developed with a broad vision of the modern construction materials world, a world where substrates are no longer uniform. Concrete meets foam, building boards integrate with 3D prints, and printed elements blend into traditional construction. The challenge is not just physical bonding between materials, but creating one unified, natural material language.',
      'The foundation of Microlit is a natural mineral mixture combined with advanced cross-linked polymers, giving the material high adhesion capability, controlled flexibility, and long-term durability. This combination allows direct application even on substrates that are not mineral in nature, including 3D prints from various materials, foamed polystyrene, lightweight building boards, and complex elements.',
      'In the world of 3D printing, Microlit enables a transition from raw technical material to natural architectural appearance. Elements printed in PLA, PETG, or other materials can receive a thin, uniform, matte mineral coating layer that hides print marks and provides a stone, plaster, or natural material feel, without compromising geometry or details.',
      'The system is not limited to coating alone. Alongside Microlit, there is a dedicated bonding materials system designed to unify different substrates and create a stable intermediate layer. This allows working on complex combinations of foam and concrete, printed elements alongside existing walls, or new additions on old structures, achieving a uniform and continuous appearance without material or color differences.',
      'Microlit is applied as a thin finish layer, similar to mineral micro-topping, but with the convenience of a ready-to-use material. No additives, complicated mixing, or special adjustments for each substrate are needed. The entire system was built so that the material "knows" how to adapt itself, and the user can focus on execution and results.',
      'The resulting finish is a natural matte finish with a lively, intentionally non-uniform mineral texture. The tone is not achieved from synthetic pigments, but from the minerals themselves: mica, clay, lime, titanium, and other natural minerals. This creates a color layer that does not look like paint, but like material.',
      'The ability to unify different substrates under one mineral layer makes Microlit a central working tool in contemporary projects: wall coverings, decorative elements, space renovation, advanced DIY work, and integration of 3D prints in construction and interior/exterior design.',
      'Microlit is not a material that hides substrates, but a material that translates them into one language. It allows working with innovative and technological materials without giving up the natural, timeless appearance of mineral material. This is a precise connection between traditional construction, advanced technology, and modern work experience, producing a uniform, calm, and correct result over time.'
    ]
  }
};

export const SystemArticle = ({ isCompact = false }) => {
  const { isRTL } = useLanguage();
  const [isExpanded, setIsExpanded] = useState(false);
  const content = isRTL ? articleContent.he : articleContent.en;

  return (
    <section 
      id="system-article"
      className={cn(
        "relative overflow-hidden",
        isCompact ? "py-4" : "py-8 md:py-12 px-4"
      )}
    >
      {/* Background */}
      {!isCompact && (
        <div className="absolute inset-0 bg-gradient-to-b from-secondary/20 via-background to-secondary/30 -z-10" />
      )}

      <div className={cn("mx-auto", isCompact ? "px-4" : "max-w-4xl")}>
        {/* Header - Always visible */}
        <motion.button
          onClick={() => setIsExpanded(!isExpanded)}
          className={cn(
            "w-full text-start group",
            isCompact 
              ? "p-4 rounded-xl bg-secondary/30 hover:bg-secondary/50 border border-border/30"
              : "p-5 sm:p-6 rounded-2xl bg-card/80 backdrop-blur-sm border border-border/50 hover:border-accent/30 shadow-sm hover:shadow-md"
          )}
          whileHover={{ scale: isCompact ? 1 : 1.005 }}
          whileTap={{ scale: 0.995 }}
          data-testid="system-article-toggle"
        >
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-start gap-3 flex-1 min-w-0">
              {!isCompact && (
                <div className="w-10 h-10 rounded-xl bg-accent/10 flex items-center justify-center flex-shrink-0">
                  <BookOpen className="w-5 h-5 text-accent" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  {isCompact && <BookOpen className="w-4 h-4 text-accent" />}
                  <span className="text-xs text-accent font-medium">
                    {content.subtitle}
                  </span>
                </div>
                <h3 className={cn(
                  "font-bold text-foreground leading-tight",
                  isCompact ? "text-sm" : "text-lg sm:text-xl"
                )}>
                  {content.title}
                </h3>
                {!isCompact && !isExpanded && (
                  <p className="text-sm text-muted-foreground mt-2 line-clamp-2">
                    {content.paragraphs[0]}
                  </p>
                )}
              </div>
            </div>
            
            <div className={cn(
              "flex items-center gap-2 flex-shrink-0 transition-colors",
              isExpanded ? "text-accent" : "text-muted-foreground group-hover:text-accent"
            )}>
              <span className="text-xs font-medium hidden sm:inline">
                {isExpanded ? content.readLess : content.readMore}
              </span>
              <motion.div
                animate={{ rotate: isExpanded ? 180 : 0 }}
                transition={{ duration: 0.2 }}
              >
                <ChevronDown className="w-5 h-5" />
              </motion.div>
            </div>
          </div>
        </motion.button>

        {/* Expanded Content */}
        <AnimatePresence>
          {isExpanded && (
            <motion.article
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.3, ease: 'easeInOut' }}
              className="overflow-hidden"
            >
              <div className={cn(
                "bg-card/60 backdrop-blur-sm border border-t-0 border-border/50 rounded-b-2xl",
                isCompact ? "p-4 pt-3" : "p-5 sm:p-8 pt-4"
              )}>
                {/* Decorative element */}
                {!isCompact && (
                  <div className="flex items-center gap-2 mb-6 pb-4 border-b border-border/30">
                    <Sparkles className="w-4 h-4 text-accent" />
                    <span className="text-sm text-muted-foreground">
                      {isRTL ? 'מידע מקצועי על המוצר' : 'Professional product information'}
                    </span>
                  </div>
                )}

                {/* Article paragraphs */}
                <div className={cn(
                  "space-y-4 text-foreground/90",
                  isCompact ? "text-sm" : "text-base leading-relaxed"
                )}>
                  {content.paragraphs.map((paragraph, index) => (
                    <motion.p
                      key={index}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.05 }}
                      className={index === 0 ? "font-medium" : ""}
                    >
                      {paragraph}
                    </motion.p>
                  ))}
                </div>

                {/* Keywords for SEO - hidden visually but present in DOM */}
                <div className="sr-only" aria-hidden="true">
                  <h4>Keywords</h4>
                  <p>Microlit, mineral coating, 3D print coating, concrete coating, foam coating, natural mineral finish, micro-topping, DIY coating, wall coating, decorative coating, PLA coating, PETG coating, natural texture, mineral paint, building materials, interior design, exterior coating</p>
                  <p>ציפוי מינרלי, מיקרוליט, ציפוי בטון, ציפוי קלקר, ציפוי הדפסת תלת מימד, גמר מינרלי, מיקרוטופינג, ציפוי קירות, DIY, עיצוב פנים, עיצוב חוץ, חומרי בנייה, טקסטורה טבעית</p>
                </div>

                {/* Close button at bottom */}
                <motion.button
                  onClick={() => setIsExpanded(false)}
                  className="mt-6 w-full py-2 text-sm text-muted-foreground hover:text-accent transition-colors flex items-center justify-center gap-2"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <ChevronDown className="w-4 h-4 rotate-180" />
                  {content.readLess}
                </motion.button>
              </div>
            </motion.article>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
};
